import { User } from 'firebase/auth';
import { checkUserMembership } from './organizations';
import { getTeam, getTeamMembership } from './teams';
import { getProject } from './projects';

/**
 * Permission System Overview:
 *
 * VIEWER ROLE:
 * - Can view all org content (teams, projects, updates)
 * - CANNOT create teams, projects, or post updates
 * - CAN participate in workflows (retros, crits, standups) if invited via ACL
 * - CAN comment and react on workflows they're invited to
 * - Intended for stakeholders who need visibility but not full participation
 *
 * MEMBER+ ROLES:
 * - Full content creation: teams, projects, updates
 * - Can comment/react anywhere
 * - Can participate in all workflows
 */

export type OrgRole = 'owner' | 'admin' | 'manager' | 'member' | 'viewer';
export type TeamRole = 'manager' | 'member' | 'viewer';

export interface PermissionContext {
  user: User | null;
  orgId?: string;
  teamId?: string;
  projectId?: string;
  targetUserId?: string;
}

export interface PermissionResult {
  allowed: boolean;
  reason?: string;
  role?: OrgRole | TeamRole;
}

export enum OrgPermission {
  DELETE_ORG = 'delete_org',
  MANAGE_BILLING = 'manage_billing',
  MANAGE_MEMBERS = 'manage_members',
  CHANGE_ROLES = 'change_roles',
  CREATE_TEAMS = 'create_teams',
  DELETE_TEAMS = 'delete_teams',
  CREATE_PROJECTS = 'create_projects',
  DELETE_PROJECTS = 'delete_projects',
  SEND_INVITATIONS = 'send_invitations',
  VIEW_AUDIT_LOGS = 'view_audit_logs',
  VIEW_ALL_CONTENT = 'view_all_content',
  CREATE_UPDATES = 'create_updates',
  COMMENT_ON_UPDATES = 'comment_on_updates',
  REACT_TO_CONTENT = 'react_to_content',
}

export enum TeamPermission {
  DELETE_TEAM = 'delete_team',
  MANAGE_TEAM_MEMBERS = 'manage_team_members',
  CREATE_TEAM_PROJECTS = 'create_team_projects',
  EDIT_TEAM_PROJECTS = 'edit_team_projects',
  VIEW_TEAM_CONTENT = 'view_team_content',
}

export enum ProjectPermission {
  DELETE_PROJECT = 'delete_project',
  EDIT_PROJECT = 'edit_project',
  MANAGE_CONTRIBUTORS = 'manage_contributors',
  VIEW_PROJECT = 'view_project',
}

const ORG_ROLE_HIERARCHY: Record<OrgRole, number> = {
  owner: 5,
  admin: 4,
  manager: 3,
  member: 2,
  viewer: 1,
};

const TEAM_ROLE_HIERARCHY: Record<TeamRole, number> = {
  manager: 3,
  member: 2,
  viewer: 1,
};

export async function checkSuperAdmin(user: User, forceRefresh = false): Promise<boolean> {
  try {
    // Force refresh the token to get the latest claims
    // This is important after custom claims are set via Admin SDK
    const idTokenResult = await user.getIdTokenResult(forceRefresh);

    // Check for permanent superAdmin status
    if (idTokenResult.claims.superAdmin === true) {
      return true;
    }

    // Check for temporary debugMode with valid expiry
    if (idTokenResult.claims.debugMode === true) {
      const debugExpires = idTokenResult.claims.debugExpires;
      if (debugExpires && typeof debugExpires === 'number' && debugExpires > Date.now()) {
        return true;
      }
    }

    return false;
  } catch {
    return false;
  }
}

export async function checkOrgPermission(
  permission: OrgPermission,
  context: PermissionContext
): Promise<PermissionResult> {
  // Default deny
  if (!context.user || !context.orgId) {
    return { allowed: false, reason: 'Authentication required' };
  }

  // Super admin bypass
  if (await checkSuperAdmin(context.user)) {
    return { allowed: true, reason: 'Super admin privileges' };
  }

  // Get user's org membership
  const membership = await checkUserMembership(context.orgId, context.user.uid);
  if (!membership) {
    return { allowed: false, reason: 'Not a member of this organization' };
  }

  // Check for soft-deleted membership
  if ('deletedAt' in membership && membership.deletedAt) {
    return { allowed: false, reason: 'Membership has been revoked' };
  }

  const userRole = membership.role as OrgRole;

  // Permission matrix
  const permissionMatrix: Record<OrgPermission, OrgRole[]> = {
    [OrgPermission.DELETE_ORG]: ['owner'],
    [OrgPermission.MANAGE_BILLING]: ['owner', 'admin'],
    [OrgPermission.MANAGE_MEMBERS]: ['owner', 'admin', 'manager'],
    [OrgPermission.CHANGE_ROLES]: ['owner', 'admin'],
    [OrgPermission.CREATE_TEAMS]: ['owner', 'admin', 'manager', 'member'], // Viewers cannot create teams
    [OrgPermission.DELETE_TEAMS]: ['owner', 'admin'],
    [OrgPermission.CREATE_PROJECTS]: ['owner', 'admin', 'manager', 'member'], // Viewers cannot create projects
    [OrgPermission.DELETE_PROJECTS]: ['owner', 'admin'],
    [OrgPermission.SEND_INVITATIONS]: ['owner', 'admin', 'manager'],
    [OrgPermission.VIEW_AUDIT_LOGS]: ['owner', 'admin'],
    [OrgPermission.VIEW_ALL_CONTENT]: ['owner', 'admin', 'manager', 'member', 'viewer'],
    [OrgPermission.CREATE_UPDATES]: ['owner', 'admin', 'manager', 'member'], // Viewers cannot post updates
    [OrgPermission.COMMENT_ON_UPDATES]: ['owner', 'admin', 'manager', 'member'], // Viewers can comment on workflows they're invited to (via ACL)
    [OrgPermission.REACT_TO_CONTENT]: ['owner', 'admin', 'manager', 'member'], // Viewers can react on workflows they're invited to (via ACL)
  };

  const allowedRoles = permissionMatrix[permission];
  const allowed = allowedRoles.includes(userRole);

  return {
    allowed,
    reason: allowed 
      ? `Permitted as ${userRole}` 
      : `Insufficient privileges. Required roles: ${allowedRoles.join(', ')}`,
    role: userRole,
  };
}

export async function checkTeamPermission(
  permission: TeamPermission,
  context: PermissionContext
): Promise<PermissionResult> {
  // Default deny
  if (!context.user || !context.orgId || !context.teamId) {
    return { allowed: false, reason: 'Authentication and team context required' };
  }

  // Super admin bypass
  if (await checkSuperAdmin(context.user)) {
    return { allowed: true, reason: 'Super admin privileges' };
  }

  // Check org-level permissions first
  const orgMembership = await checkUserMembership(context.orgId, context.user.uid);
  if (!orgMembership) {
    return { allowed: false, reason: 'Not a member of this organization' };
  }

  const orgRole = orgMembership.role as OrgRole;
  
  // Org owners and admins have full team permissions
  if (orgRole === 'owner' || orgRole === 'admin') {
    return { 
      allowed: true, 
      reason: `Organization ${orgRole} has full team permissions`,
      role: orgRole,
    };
  }

  // Check team membership
  const team = await getTeam(context.orgId, context.teamId);
  if (!team) {
    return { allowed: false, reason: 'Team not found' };
  }

  // Check if team is soft-deleted
  if (team.deletedAt) {
    return { allowed: false, reason: 'Team has been deleted' };
  }

  // Check team membership from subcollection first, fallback to memberIds array
  const teamMembership = await getTeamMembership(context.orgId, context.teamId, context.user.uid);

  let teamRole: TeamRole;
  if (teamMembership) {
    // Use role from membership subcollection
    teamRole = teamMembership.role;
  } else if (team.memberIds?.includes(context.user.uid)) {
    // Fallback to legacy memberIds array
    // Team creator gets manager role, others get member role
    teamRole = team.createdBy === context.user.uid ? 'manager' : 'member';
  } else {
    // Not a team member - check if org manager can still perform action
    if (orgRole === 'manager' &&
        [TeamPermission.DELETE_TEAM, TeamPermission.MANAGE_TEAM_MEMBERS].includes(permission)) {
      return {
        allowed: true,
        reason: 'Organization manager can manage all teams',
        role: orgRole,
      };
    }
    return { allowed: false, reason: 'Not a member of this team' };
  }

  const permissionMatrix: Record<TeamPermission, TeamRole[]> = {
    [TeamPermission.DELETE_TEAM]: ['manager'],
    [TeamPermission.MANAGE_TEAM_MEMBERS]: ['manager'],
    [TeamPermission.CREATE_TEAM_PROJECTS]: ['manager', 'member'],
    [TeamPermission.EDIT_TEAM_PROJECTS]: ['manager', 'member'],
    [TeamPermission.VIEW_TEAM_CONTENT]: ['manager', 'member', 'viewer'],
  };

  const allowedRoles = permissionMatrix[permission];
  const allowed = allowedRoles.includes(teamRole);

  return {
    allowed,
    reason: allowed 
      ? `Permitted as team ${teamRole}` 
      : `Insufficient team privileges. Required roles: ${allowedRoles.join(', ')}`,
    role: teamRole,
  };
}

export async function checkProjectPermission(
  permission: ProjectPermission,
  context: PermissionContext
): Promise<PermissionResult> {
  // Default deny
  if (!context.user || !context.orgId || !context.projectId) {
    return { allowed: false, reason: 'Authentication and project context required' };
  }

  // Super admin bypass
  if (await checkSuperAdmin(context.user)) {
    return { allowed: true, reason: 'Super admin privileges' };
  }

  // Get project
  const project = await getProject(context.orgId, context.projectId);
  if (!project) {
    return { allowed: false, reason: 'Project not found' };
  }

  // Check if soft-deleted
  if ('deletedAt' in project && project.deletedAt) {
    return { allowed: false, reason: 'Project has been deleted' };
  }

  const userId = context.user.uid;
  const isOwner = project.createdBy === userId;
  const isContributor = project.contributors?.includes(userId) || false;

  // Check org membership
  const orgMembership = await checkUserMembership(context.orgId, userId);
  if (!orgMembership) {
    return { allowed: false, reason: 'Not a member of this organization' };
  }

  const orgRole = orgMembership.role as OrgRole;

  // Org owners and admins can manage all projects
  if (orgRole === 'owner' || orgRole === 'admin') {
    return {
      allowed: true,
      reason: `Organization ${orgRole} has full project permissions`,
      role: orgRole,
    };
  }

  // Check if user is a manager on the project's team
  let isTeamManager = false;
  const projectTeamId = project.teamId;
  if (projectTeamId) {
    const teamMembership = await getTeamMembership(context.orgId, projectTeamId, userId);
    if (teamMembership?.role === 'manager') {
      isTeamManager = true;
    }
  }

  // Project-specific permissions
  switch (permission) {
    case ProjectPermission.DELETE_PROJECT:
      return {
        allowed: isOwner,
        reason: isOwner ? 'Project owner' : 'Only project owner can delete',
      };

    case ProjectPermission.MANAGE_CONTRIBUTORS:
      return {
        allowed: isOwner || isTeamManager,
        reason: isOwner
          ? 'Project owner'
          : isTeamManager
            ? 'Team manager'
            : 'Only project owner and team managers can manage contributors',
      };

    case ProjectPermission.EDIT_PROJECT:
      return {
        allowed: isOwner || isContributor || isTeamManager,
        reason: isOwner
          ? 'Project owner'
          : isContributor
            ? 'Project contributor'
            : isTeamManager
              ? 'Team manager'
              : 'Only project owner, contributors, and team managers can edit',
      };
    
    case ProjectPermission.VIEW_PROJECT:
      // TODO: Check visibility field once added
      // For now, all org members can view all projects
      return {
        allowed: true,
        reason: 'Organization member',
        role: orgRole,
      };
    
    default:
      return { allowed: false, reason: 'Unknown permission' };
  }
}

export async function canPerformAction(
  action: string,
  context: PermissionContext
): Promise<PermissionResult> {
  // Map action strings to specific permission checks
  const actionMap: Record<string, () => Promise<PermissionResult>> = {
    // Organization actions
    'org.delete': () => checkOrgPermission(OrgPermission.DELETE_ORG, context),
    'org.billing.manage': () => checkOrgPermission(OrgPermission.MANAGE_BILLING, context),
    'org.members.manage': () => checkOrgPermission(OrgPermission.MANAGE_MEMBERS, context),
    'org.roles.change': () => checkOrgPermission(OrgPermission.CHANGE_ROLES, context),
    'org.teams.create': () => checkOrgPermission(OrgPermission.CREATE_TEAMS, context),
    'org.projects.create': () => checkOrgPermission(OrgPermission.CREATE_PROJECTS, context),
    'org.invitations.send': () => checkOrgPermission(OrgPermission.SEND_INVITATIONS, context),
    
    // Team actions
    'team.delete': () => checkTeamPermission(TeamPermission.DELETE_TEAM, context),
    'team.members.manage': () => checkTeamPermission(TeamPermission.MANAGE_TEAM_MEMBERS, context),
    'team.projects.create': () => checkTeamPermission(TeamPermission.CREATE_TEAM_PROJECTS, context),
    'team.projects.edit': () => checkTeamPermission(TeamPermission.EDIT_TEAM_PROJECTS, context),
    
    // Project actions
    'project.delete': () => checkProjectPermission(ProjectPermission.DELETE_PROJECT, context),
    'project.edit': () => checkProjectPermission(ProjectPermission.EDIT_PROJECT, context),
    'project.contributors.manage': () => checkProjectPermission(ProjectPermission.MANAGE_CONTRIBUTORS, context),
    'project.view': () => checkProjectPermission(ProjectPermission.VIEW_PROJECT, context),
  };

  const checkFn = actionMap[action];
  if (!checkFn) {
    return { allowed: false, reason: `Unknown action: ${action}` };
  }

  return checkFn();
}

export function hasHigherRole(
  currentRole: OrgRole | TeamRole,
  targetRole: OrgRole | TeamRole,
  scope: 'org' | 'team'
): boolean {
  if (scope === 'org') {
    return ORG_ROLE_HIERARCHY[currentRole as OrgRole] > ORG_ROLE_HIERARCHY[targetRole as OrgRole];
  } else {
    return TEAM_ROLE_HIERARCHY[currentRole as TeamRole] > TEAM_ROLE_HIERARCHY[targetRole as TeamRole];
  }
}

export function canChangeRole(
  userRole: OrgRole,
  targetCurrentRole: OrgRole,
  targetNewRole: OrgRole
): boolean {
  // Only owners and admins can change roles
  if (userRole !== 'owner' && userRole !== 'admin') {
    return false;
  }

  // Cannot change a role to/from a higher or equal level than your own
  if (!hasHigherRole(userRole, targetCurrentRole, 'org') || 
      !hasHigherRole(userRole, targetNewRole, 'org')) {
    return false;
  }

  return true;
}